import React, { ReactElement, useMemo } from 'react';
import BillingPackageVariant from '../../atoms/billing/BillingPackageVariant';
import styled from '@emotion/styled';
import PackageListContainer from '../../atoms/billing/PackageListContainer';
import { checkVIP, isDarkMode } from '../../utils/helpers';
import PromotionalPackageContainer from '../../atoms/billing/PromotionalPackageContainer';
import BillingPackagePromoVariant from '../../atoms/billing/BillingPackagePromoVariant';
import PrimaryButton from '../../atoms/buttons/PrimaryButton';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { makeStyles, Theme } from '@material-ui/core';
import breakpoints from '../../theme/material/breakpoints';

const useStyles = makeStyles((theme: Theme) => ({
  packageListContainer: {
    margin: '0 auto',
    width: '100%',
    padding: 0,
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '70%',
      padding: '0 15%',
      maxWidth: 546,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      padding: 0,
      maxWidth: '100%',
    },
  },
  packageListVip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
    },
  },
}));

const PackageList = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PackageListStacked = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PackageListIntroCreditOffer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Line = styled('small')({
  margin: '30px auto 45px',
  display: 'block',
  fontSize: 16,
  [breakpoints.down('sm')]: {
    margin: '40px auto',
  },
  [breakpoints.down('xs')]: {
    margin: '40px auto 30px',
  },
});

const creditStyle = (isVip: boolean) => {
  return {
    background: isVip
      ? isDarkMode()
        ? 'linear-gradient(135deg, #A50000 0%, #3A0000 100%)'
        : 'linear-gradient(135deg, #CF0404 0%, #680000 100%)'
      : 'linear-gradient(135deg, #0F6CE2 0%, #E21D4A 100%)',
  };
};

interface BillingPackagesProps {
  packages: any;
  promoPackages: any;
  selected: any;
  selectPackage: any;
  stepBilling: any;
  emailPromo: any;
  showPayment: any;
  selectionOff: any;
  selectedPackage: any;
  stackedVariant: any;
  normalPromoBanner: any;
  promoCountdownExpiresAt: any;
}

const BillingPackagesVariant = ({
  packages,
  promoPackages,
  selected,
  selectPackage,
  stepBilling,
  emailPromo,
  showPayment,
  selectionOff,
  selectedPackage,
  stackedVariant,
  normalPromoBanner,
  promoCountdownExpiresAt,
}: BillingPackagesProps): ReactElement => {
  const { isMobile, isMobileXs } = useMediaBreakpoints();
  const classes = useStyles();
  const hasPromoPackage = promoPackages && promoPackages.length > 0;

  const isIntroCreditOffer = useMemo(() => {
    return promoPackages.some(pkg => pkg.identifier === 'generous_intro_credit_promo');
  }, [promoPackages]);

  const renderPackages = allPackages => {
    const VIP = ['generous_pp_special_2', 'generous_pp_special_1'];
    let isVIP = '';
    let isPromotionalPgk = false;
    const filteredCards = allPackages.reverse().filter(pkg => pkg.packageVersion !== 'new');

    const cards = filteredCards.map((pkg, index) => {
      const recommended = !hasPromoPackage && pkg.recommended;

      isVIP = VIP.find(ele => ele === pkg.identifier);
      isPromotionalPgk = pkg.promotional;

      return isVIP || pkg.promotional ? (
        <BillingPackagePromoVariant
          index={index}
          key={pkg.identifier}
          selectPackage={selectPackage}
          credits={pkg.credits}
          identifier={pkg.identifier}
          currencyCharCode={pkg.currency.data.label_unicode}
          price={pkg.price}
          variant={isIntroCreditOffer ? 'intro_credit_promo' : isVIP ? 'vip' : 'special'}
        />
      ) : (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <BillingPackageVariant
          index={index}
          key={pkg.identifier}
          selected={selected === pkg.identifier}
          selectPackage={selectPackage}
          credits={parseFloat(pkg.credits)}
          promotional={pkg.promotional}
          identifier={pkg.identifier}
          currencyCharCode={pkg.currency.data.label_unicode}
          price={pkg.price}
          isRecommended={recommended}
          vatDetails={pkg.taxes}
          stepBilling={stepBilling}
          emailPromo={emailPromo}
          showPayment={showPayment}
          selectionOff={selectionOff}
          name={pkg.name}
          appliedPromoDiscount={pkg?.discount ?? {}}
        />
      );
    });

    const renderCards = () => {
      switch (true) {
        case isIntroCreditOffer:
          return <PackageListIntroCreditOffer id="packageList">{cards}</PackageListIntroCreditOffer>;
        case (isVIP || isPromotionalPgk) && stackedVariant && !isIntroCreditOffer:
          return <PackageListStacked id="packageList">{cards}</PackageListStacked>;
        case (isVIP || isPromotionalPgk) && !isIntroCreditOffer:
          return (
            <div className={classes.packageListVip} id="packageList">
              {cards}
            </div>
          );
        default:
          return (
            <PackageList style={{ minHeight: isMobile ? 250 : 'unset' }} id="packageList">
              {cards}
            </PackageList>
          );
      }
    };

    return (
      <React.Fragment>
        {renderCards()}
        {!isVIP && !isPromotionalPgk && (
          <PrimaryButton
            size="regular"
            color="red"
            onClick={e => {
              e.stopPropagation();
              if (stepBilling) {
                showPayment(selectedPackage, emailPromo);
              }
            }}
            data-test-id="continue-package-payment"
            label="Continue"
            customStyle={{
              fontSize: 16,
              marginTop: isMobile ? 30 : 45,
              width: 'inherit',
              height: 44,
              padding: '0px 25px',
              fontWeight: 700,
              minWidth: 212,
            }}
          />
        )}
      </React.Fragment>
    );
  };

  const isPromo = hasPromoPackage && promoPackages.length > 0;

  const setPackageStylePromo = isVip => {
    const { background } = creditStyle(isVip);

    return {
      maxWidth: isMobileXs ? 599 : 780,
      background,
      padding: isMobileXs ? '20px 10px 25px 10px' : '20px 15px 30px',
      display: 'grid',
      margin: isMobile ? '0px -10px 0px' : 'auto',
      borderRadius: isMobileXs ? 0 : 10,
    };
  };

  return (
    <div className={classes.packageListContainer} data-test-id="package-list">
      {isPromo && (
        <PackageListContainer style={setPackageStylePromo(checkVIP(promoPackages))} maxWidth={'100%'}>
          <PromotionalPackageContainer
            countDown={promoCountdownExpiresAt}
            promoVariant={isIntroCreditOffer ? 'intro_credit_promo' : checkVIP(promoPackages) ? 'vip' : 'special'}
          />
          {renderPackages(promoPackages.reverse())}
        </PackageListContainer>
      )}
      {normalPromoBanner}
      {hasPromoPackage && packages.length > 0 ? (
        <Line
          style={
            normalPromoBanner && isMobile
              ? {
                  margin: '40px auto',
                }
              : {}
          }
        >
          – Other Credit Packages –
        </Line>
      ) : null}
      <PackageListContainer>{packages.length > 0 ? renderPackages(packages) : null}</PackageListContainer>
    </div>
  );
};

export default BillingPackagesVariant;
